import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';

export default function SubscriptionPlans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [subscribing, setSubscribing] = useState(null);
  const navigate = useNavigate();
  const { refreshCompany } = useAuth();

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get('/subscription-plans');
      setPlans(response.data.plans);
    } catch (err) {
      setError('Failed to load subscription plans');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planId) => {
    setSubscribing(planId);
    setError('');

    try {
      await axios.post('/subscriptions/subscribe', {
        subscription_plan_id: planId,
      });
      await refreshCompany();
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to subscribe. Please try again.');
    } finally {
      setSubscribing(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 8, mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom align="center">
          Choose Your Plan
        </Typography>
        <Typography variant="h6" color="text.secondary" align="center" sx={{ mb: 4 }}>
          Select the perfect plan for your hiring needs
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={4}>
          {plans.map((plan) => (
            <Grid item xs={12} md={6} lg={3} key={plan.id}>
              <Card
                elevation={plan.name === 'Professional' ? 8 : 2}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  border: plan.name === 'Professional' ? 2 : 0,
                  borderColor: 'primary.main',
                }}
              >
                {plan.name === 'Professional' && (
                  <Chip
                    label="POPULAR"
                    color="primary"
                    size="small"
                    sx={{ position: 'absolute', top: 10, right: 10 }}
                  />
                )}
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  <Typography variant="h5" component="h2" gutterBottom>
                    {plan.name}
                  </Typography>
                  <Box sx={{ my: 2 }}>
                    <Typography variant="h3" component="span" color="primary">
                      ${plan.price}
                    </Typography>
                    <Typography variant="body2" component="span" color="text.secondary">
                      /{plan.billing_cycle === 'yearly' ? 'year' : 'month'}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {plan.description}
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircleIcon color="success" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={`Up to ${plan.max_interviewers} ${plan.max_interviewers === 1 ? 'interviewer' : 'interviewers'}`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircleIcon color="success" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${plan.max_interviews_per_month} interviews/month`}
                      />
                    </ListItem>
                    {plan.recording_enabled && (
                      <ListItem>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircleIcon color="success" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Session recording" />
                      </ListItem>
                    )}
                    {plan.analytics_enabled && (
                      <ListItem>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircleIcon color="success" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Analytics & insights" />
                      </ListItem>
                    )}
                  </List>
                  <Button
                    fullWidth
                    variant={plan.name === 'Professional' ? 'contained' : 'outlined'}
                    size="large"
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={subscribing !== null}
                    sx={{ mt: 2 }}
                  >
                    {subscribing === plan.id ? 'Subscribing...' : 'Subscribe'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Container>
  );
}

